import { PageContainer } from '../../components/PageContainer';
import { Button } from '../../components/ui/Button';
import { Typography } from '../../components/ui/Typography';
import { WarningTriangleIcon, InfoCircleIcon } from '../../components/ui/icons';
import type { BackNavigation } from './types';

interface WineDetailErrorProps {
  error: unknown;
  backNavigation: BackNavigation;
  onRetry: () => void;
  isRetrying: boolean;
  retryCount: number;
  isNetworkError: (error: unknown) => boolean;
}

export const WineDetailError = ({ 
  error, 
  backNavigation, 
  onRetry, 
  isRetrying, 
  retryCount,
  isNetworkError 
}: WineDetailErrorProps) => {
  const errorObj = error as { message?: string; data?: { code?: string } };
  
  // Check if this is a 404 error (wine not found)
  const isNotFound = errorObj.message?.includes('not found') || 
                    errorObj.message?.includes('404') ||
                    errorObj.data?.code === 'NOT_FOUND';

  if (isNotFound) {
    return (
      <div className="px-0 md:px-6 lg:px-8 pt-2 pb-4 max-w-4xl lg:max-w-5xl xl:max-w-6xl 2xl:max-w-7xl mx-auto">
        <PageContainer className="mt-2">
          <div className="text-center py-8 md:py-12 px-4">
            <div className="mx-auto flex items-center justify-center h-14 w-14 md:h-16 md:w-16 rounded-full bg-red-100 mb-4">
              <WarningTriangleIcon className="h-7 w-7 md:h-8 md:w-8 text-red-600" />
            </div>
            <Typography 
              variant="heading2" 
              as="h1" 
              className="mb-2"
            >
              Wine Not Found
            </Typography>
            <Typography 
              variant="bodyMedium" 
              as="p" 
              className="text-gray-600 mb-6 px-2"
            >
              The wine you're looking for doesn't exist or may have been removed.
            </Typography>
            <div className="space-y-3">
              <a
                href="/wines"
                className="inline-flex items-center px-6 py-3 border border-transparent text-sm md:text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-info-500 min-h-[44px] touch-manipulation"
              >
                Browse All Wines
              </a>
              <div>
                <a
                  href="/wineries"
                  className="inline-flex items-center px-6 py-3 border border-gray-300 text-sm md:text-base font-medium rounded-md text-gray-700 bg-gray-50 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-info-500 min-h-[44px] touch-manipulation"
                >
                  Explore Wineries
                </a>
              </div>
            </div>
          </div>
        </PageContainer>
      </div>
    );
  }

  // Network errors with retry functionality
  if (isNetworkError(error)) {
    return (
      <div className="px-0 md:px-6 lg:px-8 pt-2 pb-4 max-w-4xl lg:max-w-5xl xl:max-w-6xl 2xl:max-w-7xl mx-auto">
        <PageContainer className="mt-2">
          <div className="text-center py-8 md:py-12 px-4">
            <div className="mx-auto flex items-center justify-center h-14 w-14 md:h-16 md:w-16 rounded-full bg-yellow-100 mb-4">
              <InfoCircleIcon className="h-7 w-7 md:h-8 md:w-8 text-yellow-600" />
            </div>
            <Typography 
              variant="heading2" 
              as="h1" 
              className="mb-2"
            >
              Connection Problem
            </Typography>
            <Typography 
              variant="bodyMedium" 
              as="p" 
              className="text-gray-600 mb-2 px-2"
            >
              Unable to load wine details due to a network issue.
            </Typography>
            <Typography 
              variant="caption" 
              as="p" 
              className="text-gray-500 mb-6 px-2"
            >
              This may affect the wine information, hero image, and additional images.
              {retryCount > 0 && ` (Attempt ${retryCount + 1})`}
            </Typography>
            <div className="space-y-3">
              <Button
                variant="primary"
                onClick={onRetry}
                loading={isRetrying}
                className="min-h-[44px] px-6 py-3 touch-manipulation"
              >
                Try Again
              </Button>
              <div>
                <a
                  href="/wines"
                  className="inline-flex items-center px-6 py-3 border border-gray-300 text-sm md:text-base font-medium rounded-md text-gray-700 bg-gray-50 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-info-500 min-h-[44px] touch-manipulation"
                >
                  Back to Wines
                </a>
              </div>
            </div>
          </div>
        </PageContainer>
      </div>
    );
  }

  // Other errors (server errors, etc.)
  return (
    <div className="px-0 md:px-6 lg:px-8 pt-2 pb-4 max-w-4xl lg:max-w-5xl xl:max-w-6xl 2xl:max-w-7xl mx-auto">
      <PageContainer className="mt-2">
        <div className="text-center py-8 md:py-12 px-4">
          <div className="mx-auto flex items-center justify-center h-14 w-14 md:h-16 md:w-16 rounded-full bg-red-100 mb-4">
            <InfoCircleIcon className="h-7 w-7 md:h-8 md:w-8 text-red-600" />
          </div>
          <Typography 
            variant="heading2" 
            as="h1" 
            className="mb-2"
          >
            Error Loading Wine
          </Typography>
          <Typography 
            variant="bodyMedium" 
            as="p" 
            className="text-gray-600 mb-6 px-2"
          >
            {errorObj.message || 'An unexpected error occurred while loading the wine details, hero image, or additional content.'}
          </Typography>
          <div className="space-y-3">
            <Button
              variant="primary"
              onClick={onRetry}
              loading={isRetrying}
              className="min-h-[44px] px-6 py-3 touch-manipulation"
            >
              Try Again
            </Button>
            <div>
              <a
                href="/wines"
                className="inline-flex items-center px-6 py-3 border border-gray-300 text-sm md:text-base font-medium rounded-md text-gray-700 bg-gray-50 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-info-500 min-h-[44px] touch-manipulation"
              >
                Back to Wines
              </a>
            </div>
          </div>
        </div>
      </PageContainer>
    </div>
  );
};